/**
 * Droite à curseurs — calculs purs pour f(x) = ax + b.
 *
 * a, b et les lectures au doigt sont au pas de 0,5 : images et antécédents
 * sont donc des rationnels, écrits exactement (« 2,5 », « 7/3 »).
 */

import { rational, type ExactNumber } from '../../ui/exact';
import { formatNumber } from '../../ui/format';

export const PARAM_STEP = 0.5;
export const PARAM_MIN = -5;
export const PARAM_MAX = 5;

export function affine(a: number, b: number, x: number): number {
  return a * x + b;
}

/** « 2x + 1 », « −x », « 0,5x − 3 », « 4 » */
export function affineText(a: number, b: number): string {
  const n = (v: number) => formatNumber(v, 2);
  let ax = '';
  if (a === 1) ax = 'x';
  else if (a === -1) ax = '−x';
  else if (a !== 0) ax = `${n(a)}x`;
  if (!ax) return n(b);
  if (b === 0) return ax;
  return `${ax} ${b > 0 ? '+' : '−'} ${n(Math.abs(b))}`;
}

export type Nature = 'lineaire' | 'constante' | 'affine';

export function nature(a: number, b: number): Nature {
  if (a === 0) return 'constante';
  if (b === 0) return 'lineaire';
  return 'affine';
}

export { rational, type ExactNumber };

/** Antécédent de y : (y − b)/a, exact ; null si a = 0. Entrées au pas de 0,5. */
export function antecedent(a: number, b: number, y: number): ExactNumber | null {
  if (a === 0) return null;
  // ×2 : tout devient entier
  return rational(Math.round(2 * (y - b)), Math.round(2 * a));
}

/** Image exacte f(x) (multiple de 0,25). */
export function image(a: number, b: number, x: number): ExactNumber {
  return rational(Math.round(4 * affine(a, b, x)), 4);
}

/** Défi : une droite à retrouver (a non nul, au pas de 0,5 ; b entier). */
export function pickTarget(
  rng: () => number,
  previous?: { a: number; b: number },
): { a: number; b: number } {
  const slopes = [-3, -2, -1.5, -1, -0.5, 0.5, 1, 1.5, 2, 3];
  for (let i = 0; i < 20; i++) {
    const a = slopes[Math.min(slopes.length - 1, Math.floor(rng() * slopes.length))]!;
    const b = Math.min(4, Math.floor(rng() * 9) - 4);
    if (!previous || previous.a !== a || previous.b !== b) return { a, b };
  }
  return { a: 1, b: previous?.b === 0 ? 1 : 0 };
}

export const CHALLENGE_ROUNDS = 5;
